import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Link from '../Link/Link'
import { RootState } from '../../redux/reducers/rootReducer'
import { LayoutStyle, Taxon } from '../../typings/base'
import {
  Paper,
  Typography,
  Grid,
  List,
  ListItem,
  Slide,
  Button,
  makeStyles,
  createStyles,
  Theme,
  GridSize,
} from '@material-ui/core'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'
import clsx from 'clsx'
import { menuUseStyles } from './Menu'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItemRelative: {
      position: 'relative',
    },
    submenuAbsolute: {
      position: 'absolute',
      left: 0,
      top: '100%',
      zIndex: 10,
      minWidth: 'max-content',
      background: '#fff',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      padding: theme.spacing(2, 2, 2, 2),
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    rowItem: {
      padding: theme.spacing(0.5, 0),
      lineHeight: 1.7,
      fontSize: 15,
    },
    columnHeader: {
      fontWeight: 800,
      marginBottom: theme.spacing(1),
      fontSize: 16,
    },
    subMenuItem: {
      listStyle: 'none',
      margin: '0.7rem 0',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    subMenuLink: {
      color: theme.palette.common.black,
      '&:hover, &:focus': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
      },
    },
    menuLink: {
      fontWeight: 1000,
      '&:hover, &:focus': {
        textDecoration: 'underline',
      },
    },
  })
)

type Props = {
  origin: string
  isMobile?: boolean
  LayoutStyle: LayoutStyle
}

const TaxonsMenu: React.FC<Props> = ({ origin, isMobile, LayoutStyle }) => {
  const classes = menuUseStyles(LayoutStyle)
  const local = useStyles()
  const taxons = useSelector(
    (state: RootState) => state.taxons.items?.[0]?.children || []
  )

  // Desktop submenu open/close state
  const [subMenuOpen, setSubMenuOpen] = useState<number | null>(null)
  const [subMenuStyle, setSubMenuStyle] = useState(false)

  // Mobile navigation state
  const [mobileStack, setMobileStack] = useState<Taxon[][]>([taxons])
  const [mobilePath, setMobilePath] = useState<Taxon[]>([])

  const buildLink = (slugs: string[]) => `${origin}/${slugs.join('/')}`

  // Desktop: handle submenu open/close
  const handleSubMenu = (idx: number) => {
    setSubMenuOpen(idx)
    setTimeout(() => {
      setSubMenuStyle(true)
    }, 300)
  }
  const handleCloseSubMenu = () => {
    setSubMenuOpen(null)
    setSubMenuStyle(false)
  }

  // Mobile: handle navigation
  const handleMobileNext = (taxon: Taxon) => {
    if (taxon.children && taxon.children.length > 0) {
      setMobileStack([...mobileStack, taxon.children])
      setMobilePath([...mobilePath, taxon])
    }
  }
  const handleMobileBack = () => {
    if (mobileStack.length > 1) {
      setMobileStack(mobileStack.slice(0, -1))
      setMobilePath(mobilePath.slice(0, -1))
    }
  }

  // Utility function to check if all children have no children
  const allChildrenAreLeaf = (children: Taxon[]) =>
    children.every((c) => !c.children || c.children.length === 0)

  // --- Desktop Render ---
  if (!isMobile) {
    return (
      <nav className={clsx(classes.menuRoot, 'menu')}>
        <ul className={clsx(classes.menuList, 'menu__list')}>
          {taxons.map((level0: Taxon, idx0: number) => (
            <li
              key={level0.code}
              className={clsx(
                classes.menuItem,
                'menu__item',
                local.menuItemRelative
              )}
              onMouseEnter={() => handleSubMenu(idx0)}
              onMouseLeave={handleCloseSubMenu}
              onFocus={() => handleSubMenu(idx0)}
              onBlur={handleCloseSubMenu}
              tabIndex={0}
            >
              <Link href={buildLink([level0.slug])}>
                <a className={clsx(classes.menuLink, 'menu__link')}>
                  {level0.name}
                </a>
              </Link>
              {subMenuOpen === idx0 &&
                level0.children &&
                level0.children.length > 0 && (
                  <div
                    className={clsx(
                      classes.submenu,
                      'menu__submenu',
                      local.submenuAbsolute,
                      subMenuOpen === idx0 &&
                        subMenuStyle &&
                        classes.subMenuOpen
                    )}
                  >
                    {allChildrenAreLeaf(level0.children) ? (
                      <ul style={{ margin: 0, padding: 0 }}>
                        {level0.children.map((level1: Taxon) => (
                          <li className={classes.subMenuItem} key={level1.code}>
                            <Link href={buildLink([level0.slug, level1.slug])}>
                              <a className={classes.subMenuLink}>
                                {level1.name}
                              </a>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Paper elevation={3}>
                        <Grid
                          container
                          spacing={2}
                          className={classes.tableGrid}
                        >
                          {level0.children.map((level1: Taxon) => (
                            <Grid
                              item
                              xs={
                                (12 / (level0.children.length || 1)) as GridSize
                              }
                              key={level1.code}
                            >
                              <Typography
                                className={clsx(
                                  classes.columnHeader,
                                  local.menuLink
                                )}
                              >
                                <Link
                                  href={buildLink([level0.slug, level1.slug])}
                                >
                                  <a
                                    className={clsx(
                                      classes.menuLink,
                                      'menu__link'
                                    )}
                                  >
                                    {level1.name}
                                  </a>
                                </Link>
                              </Typography>
                              {level1.children && level1.children.length > 0 && (
                                <div>
                                  {level1.children.map((level2: Taxon) => (
                                    <div
                                      className={classes.rowItem}
                                      key={level2.code}
                                    >
                                      <Link
                                        href={buildLink([
                                          level0.slug,
                                          level1.slug,
                                          level2.slug,
                                        ])}
                                      >
                                        <a
                                          className={clsx(
                                            classes.menuLink,
                                            local.menuLink,
                                            'menu__link'
                                          )}
                                          style={{ fontWeight: 400 }}
                                        >
                                          {level2.name}
                                        </a>
                                      </Link>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    )}
                  </div>
                )}
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  // --- Mobile Render ---
  const currentLevel = mobileStack[mobileStack.length - 1]
  const parentTaxon = mobilePath[mobilePath.length - 1]
  const parentSlugs = mobilePath.map((t) => t.slug)

  return (
    <div className={clsx(classes.rootMobile, 'menu', 'menu--mobile')}>
      <Slide in={mobileStack.length > 1} direction="left">
        <div className={mobileStack.length === 1 ? classes.hide : undefined}>
          <div className={classes.subMenuMobile}>
            <List>
              <ListItem
                className={clsx(classes.subMenuMobileBack, 'menu-mobile__back')}
              >
                <Button
                  size="small"
                  onClick={handleMobileBack}
                  startIcon={<KeyboardArrowLeft />}
                  className={classes.subMenuMobileBackBtn}
                  fullWidth
                  disableRipple
                  disableTouchRipple
                  disableElevation
                >
                  {parentTaxon ? parentTaxon.name : ''}
                </Button>
              </ListItem>
              {currentLevel.map((taxon, idx) => (
                <ListItem
                  key={taxon.code}
                  className={clsx(classes.menuMobileItem, 'menu-mobile__item')}
                  onClick={() =>
                    taxon.children &&
                    taxon.children.length > 0 &&
                    handleMobileNext(taxon)
                  }
                >
                  {taxon.children && taxon.children.length > 0 ? (
                    <>
                      <span
                        className={clsx(classes.menuMobileLink, 'menu__link')}
                      >
                        {taxon.name}
                      </span>
                      <KeyboardArrowRight />
                    </>
                  ) : (
                    <Link href={buildLink([...parentSlugs, taxon.slug])}>
                      <a className={clsx(classes.menuMobileLink, 'menu__link')}>
                        {taxon.name}
                      </a>
                    </Link>
                  )}
                </ListItem>
              ))}
            </List>
          </div>
        </div>
      </Slide>
      <div className={mobileStack.length > 1 ? classes.hide : undefined}>
        <List className={clsx('menu__list', 'menu__list--mobile')}>
          {currentLevel.map((taxon, idx) => (
            <ListItem
              key={taxon.code}
              className={clsx(classes.menuMobileItem, 'menu-mobile__item')}
              onClick={() => handleMobileNext(taxon)}
            >
              <span className={clsx(classes.menuMobileLink, 'menu__link')}>
                {taxon.name}
              </span>
              {taxon.children && taxon.children.length > 0 ? (
                <KeyboardArrowRight />
              ) : (
                <Link href={buildLink([...parentSlugs, taxon.slug])}>
                  <a className={clsx(classes.menuMobileLink, 'menu__link')}>
                    {taxon.name}
                  </a>
                </Link>
              )}
            </ListItem>
          ))}
        </List>
      </div>
    </div>
  )
}

export default TaxonsMenu
